import http from "@/services/http";
import { userTimeZone } from "@/utils/datetime";

/**
 * salvia-779 — o sistema "proativo" da reunião de 13/08.
 *
 * Dois caminhos levam ao MESMO popup (ConsultaAlertaDialog, global no App.vue):
 *
 *   "proxima"  o médico acabou de salvar uma consulta e há outra pendente hoje;
 *   "inicio"   a notificação `appointment.starting` chegou (backend avisa ~5 min
 *              antes) e o médico NÃO está em atendimento.
 *
 * Se ele estiver atendendo, o aviso de início não interrompe: a notificação
 * continua no sino, e ao salvar a consulta o popup de "próxima" cobre o caso.
 */

/** Status de quem ainda vai ser atendido hoje. */
const PENDENTES = ["scheduled", "confirmed", "waiting"];

/**
 * Atrasada ainda conta como próxima, até este limite: a consulta das 14h com o
 * médico saindo da anterior às 14h10 é exatamente o caso que a task descreve.
 */
const TOLERANCIA_ATRASO_MS = 30 * 60 * 1000;

/**
 * A próxima consulta a oferecer, entre as de hoje.
 *
 * Quem está "Em Espera" vem primeiro — o paciente já chegou, e é ele que o
 * médico chama. Depois, a de horário mais cedo.
 */
export function escolherProxima(eventos, patientIdAtual, agora = Date.now()) {
  const candidatas = (eventos || []).filter((e) => {
    if (!e?.patient?.id || !PENDENTES.includes(e.status)) return false;
    if (String(e.patient.id) === String(patientIdAtual)) return false;

    const inicio = new Date(e.start).getTime();
    return Number.isFinite(inicio) && inicio >= agora - TOLERANCIA_ATRASO_MS;
  });

  candidatas.sort((a, b) => {
    const esperaA = a.status === "waiting" ? 0 : 1;
    const esperaB = b.status === "waiting" ? 0 : 1;
    if (esperaA !== esperaB) return esperaA - esperaB;
    return new Date(a.start) - new Date(b.start);
  });

  return candidatas[0] || null;
}

const state = () => ({
  emAtendimento: false,
  alerta: null,
});

const mutations = {
  SET_EM_ATENDIMENTO(state, valor) {
    state.emAtendimento = !!valor;
  },
  SET_ALERTA(state, alerta) {
    state.alerta = alerta;
  },
  LIMPAR_ALERTA(state) {
    state.alerta = null;
  },
};

const actions = {
  /** Depois de salvar a consulta de `patientIdAtual`: há outra hoje? */
  async oferecerProxima({ commit }, { patientIdAtual }) {
    try {
      const { data } = await http.get("/api/events/today", {
        params: { time_zone: userTimeZone() },
      });
      const proxima = escolherProxima(data?.events, patientIdAtual);
      if (!proxima) return;

      commit("SET_ALERTA", {
        motivo: "proxima",
        eventId: proxima.id,
        patientId: proxima.patient.id,
        patientName: proxima.patient.name,
        patientPhoto: proxima.patient.photo || null,
        start: proxima.start,
        emEspera: proxima.status === "waiting",
        notificationId: null,
      });
    } catch (e) {
      // Oferta é conveniência: falhar aqui não pode parecer erro da consulta,
      // que já foi salva.
      console.warn("[atendimento] não foi possível buscar a próxima consulta:", e);
    }
  },

  /** O aviso que está no popup foi lido em outro lugar: some daqui também. */
  notificacaoLida({ commit, state }, notificationId) {
    if (notificationId && state.alerta?.notificationId === notificationId) {
      commit("LIMPAR_ALERTA");
    }
  },

  /**
   * O aviso chegou com o Salvia FECHADO (ou em outra máquina): ao abrir, o
   * sino o mostra, mas o popup — que só nascia na chegada ao vivo — não.
   *
   * Aqui ele aparece se ainda serve para algo: aviso NÃO lido, de consulta que
   * começa nos próximos minutos ou começou há até 30 min (a mesma tolerância
   * de atraso da "próxima consulta"). Aviso velho, ou já visto, fica só no sino.
   */
  alertarPendente({ dispatch, state }, notificacoes) {
    if (state.emAtendimento || state.alerta) return;

    const agora = Date.now();
    const pendente = (notificacoes || [])
      .filter((n) => {
        if (n?.type !== "appointment.starting" || n.is_read) return false;
        const inicio = new Date(n.data?.event_start).getTime();
        return (
          Number.isFinite(inicio) &&
          inicio >= agora - TOLERANCIA_ATRASO_MS &&
          inicio <= agora + 10 * 60 * 1000
        );
      })
      .sort((a, b) => new Date(b.data.event_start) - new Date(a.data.event_start))[0];

    if (pendente) dispatch("alertarInicio", pendente);
  },

  /** Chegou a notificação `appointment.starting` (via Echo). */
  alertarInicio({ commit, state }, notification) {
    if (state.emAtendimento) return;

    const data = notification?.data || {};
    if (!data.patient_id) return;

    commit("SET_ALERTA", {
      motivo: "inicio",
      eventId: data.event_id ?? null,
      patientId: data.patient_id,
      patientName: null,
      texto: notification.body || null,
      patientPhoto: null,
      start: data.event_start || null,
      emEspera: false,
      notificationId: notification.id ?? null,
    });
  },
};

export default { namespaced: true, state, mutations, actions };
